<script setup lang="ts">
import { cn } from '@/lib/utils'
import { Search } from 'lucide-vue-next'
import { ComboboxInput, type ComboboxInputProps, useForwardProps } from 'radix-vue'
import { computed, useAttrs, type HTMLAttributes } from 'vue'

defineOptions({
  inheritAttrs: false,
})

const props = defineProps<ComboboxInputProps & {
  class?: HTMLAttributes['class']
}>()

const delegatedProps = computed(() => {
  const { class: _, ...delegated } = props

  return delegated
})

const forwardedProps = useForwardProps(delegatedProps)

// Callers rarely pass a label: fall back to the placeholder so the field is never unnamed
const attrs = useAttrs()
const accessibleName = computed(() =>
  (attrs['aria-label'] as string | undefined) ?? (attrs.placeholder as string | undefined),
)
</script>

<template>
  <!-- The input has no outline: a 2px primary underline marks focus -->
  <div class="flex items-center border-b px-3 focus-within:shadow-[inset_0_-2px_0_hsl(var(--primary))]" cmdk-input-wrapper>
    <Search class="me-2 h-4 w-4 shrink-0 text-subtle-foreground" aria-hidden="true" />
    <ComboboxInput
      v-bind="{ ...forwardedProps, ...$attrs }"
      :aria-label="accessibleName"
      auto-focus
      :class="cn('flex h-11 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50', props.class)"
    />
  </div>
</template>
